<template>
	<v-chart
		ref="chart"
		class="chart"
		:option="option"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>

	<pop-up-modal
		v-model:show="showSettings"
		size="modal-sm"
		title="Налаштування графіка"
		@reject="showSettings = false"
	>
		<template v-slot:body>
			<div class="form-check">
				<input
					class="form-check-input"
					type="checkbox"
					id="curent"
					@change="(value) => setLineVisibility('curent', value.target.checked)"
					:checked="isLineVisible('curent')"
				>
				<label class="form-check-label" for="curent">
					{{ $options.CURENT_AXIS_NAME }}
				</label>
			</div>
			<div class="form-check">
				<input
					class="form-check-input"
					type="checkbox"
					id="voltage"
					@change="(value) => setLineVisibility('voltage', value.target.checked)"
					:checked="isLineVisible('voltage')"
				>
				<label class="form-check-label" for="voltage">
					{{ $options.VOLTAGE_AXIS_NAME }}
				</label>
			</div>
			<div class="form-check">
				<input
					class="form-check-input"
					type="checkbox"
					id="potential"
					@change="(value) => setLineVisibility('potential', value.target.checked)"
					:checked="isLineVisible('potential')"
					:disabled="isNoPotentialSource"
				>
				<label class="form-check-label" for="potential">
					{{ $options.POTENTIAL_AXIS_NAME }}
				</label>
			</div>
		</template>
	</pop-up-modal>
</template>




<script>
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
// https://echarts.apache.org/handbook/en/concepts/dataset/#
import VChart from 'vue-echarts'
import PopUpModal from "@/components/PopUpModal.vue"
import { Station } from '@/models/station.js'
import { PowerUnit } from '@/models/powerUnit.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { useStationsStore } from '@/stores/StationsStore'
import { useSessionStore } from '@/stores/SessionStore.js'
import { INFLUXDB, readStationMetrics } from '@/api/api.js'
import { chartTimeStr, chartDateStr, periodDays, } from '@/helpers.js'

import { use } from 'echarts/core'
import {
	LineChart
} from 'echarts/charts'
import {
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	ToolboxComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	MarkLineComponent
} from 'echarts/components'
import {
	CanvasRenderer,
	SVGRenderer,
} from 'echarts/renderers'

use([
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	ToolboxComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	MarkLineComponent,
	LineChart,
	CanvasRenderer,
	SVGRenderer,
])

let xAxisDayTmp = null

export default {
	// Line indexes
	Vi: 0,
	VconfidenceLineIndex: 1,
	VconfidenceRangeIndex: 2,
	Ii: 3,
	IconfidenceLineIndex: 4,
	IconfidenceRangeIndex: 5,
	Ei: 6,
	EconfidenceLineIndex: 7,
	EconfidenceRangeIndex: 8,
	
	// Y Axis Indexes
	vAxisIndex: 0,
	iAxisIndex: 1,
	eAxisIndex: 2,
	// Limitations
	VOLTAGE_MAX_DEFAULT: 10,
	CURENT_MAX_DEFAULT: 10,
	// Colors
	TITLE_COLOR: '#2c3e50',
	VOLTAGE_COLOR: '#ff994d',
	CURENT_COLOR: '#b877d9',
	RED: '#f2495c',
	ORANGE: '#ff9830',
	BLUE: '#5794f2',
	POTENTIAL_COLOR: '#5794f2',
	// Defaults
	eAxisRange: [-5, 2],
	//
	VOLTAGE_AXIS_NAME: 'Вихідна напруга',
	CURENT_AXIS_NAME: 'Вихідний струм',
	POTENTIAL_AXIS_NAME: 'Захисний потенціал',

	props: {
		stationId: {
			type: String,
			require: true,
		},
		timeFrom: {
			type: Date,
			require: false,
			default: () => {
				const now = new Date()
				now.setDate(now.getDate() - 1)
				//now.setHours(now.getHours() - 4)
				return now
			},
		},
		timeTo: {
			type: Date,
			require: false,
			default: new Date(),
		},
		legendPosition: { // top, bottom
			type: String,
			required: false,
			default: 'bottom',

		},
		isDataZoom: {
			type: Boolean,
			require: false,
			default: true,
		},
		isExpandable: {
			type: Boolean,
			require: false,
			default: false,
		},
		isExpand: {
			type: Boolean,
			require: false,
			default: false,
		},
		isTitle:  {
			type: Boolean,
			require: false,
			default: true,
		},
	},

	emits: {
		'expand': value => typeof value === "boolean",
		'collapse': value => typeof value === "boolean",
		'loading': value => typeof value === "boolean",
	},

	components: {
		VChart,
		PopUpModal,
	},

	setup() {
		const stationsStore = useStationsStore()
		const sessionStore = useSessionStore()

		return {
			stationsStore,
			sessionStore,
		}
	},

	data() {
		return {
			// Graph configuration
			option: {
				color: [
					this.$options.VOLTAGE_COLOR,
					this.$options.VOLTAGE_COLOR,
					this.$options.VOLTAGE_COLOR,
					this.$options.CURENT_COLOR,
					this.$options.CURENT_COLOR,
					this.$options.CURENT_COLOR,
					this.$options.POTENTIAL_COLOR,
					this.$options.POTENTIAL_COLOR,
					this.$options.POTENTIAL_COLOR,
				],
				title: [
					{
						show: this.isTitle,
						//top: '0%',
						left: 'center',
						text: 'Вихідні струм, напруга, захисний потенціал',
						textStyle: {
							color: this.$options.TITLE_COLOR,
							fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
							fontStyle: 'normal',
							fontSize: 14.28,
							fontWeight: 700,
						},
						textAlign: 'auto',
						padding: [8, 100],
					}
				],
				legend: {
					type: 'scroll',
					show: false, // will show after position set
					// left: 40,
					right: 'center',
					// bottom: 5,
					// top: 5,
					selectedMode: 'multiple', // can select multiple lines for show
					// Show only base lines (without min and max)
					data: [
						{
							name: this.$options.VOLTAGE_AXIS_NAME,
							textStyle: {
								width: 105,
								overflow: 'truncate',
							},
						},
						{
							name: this.$options.CURENT_AXIS_NAME,
							textStyle: {
								width: 105,
								overflow: 'truncate',
							},
						},
						{
							name: this.$options.POTENTIAL_AXIS_NAME,
							textStyle: {
								width: 190,
								overflow: 'truncate',
							},
						},
					]
				},
				dataZoom: [
					// Slider on bottom of the chart
					{
						type: 'slider',
						show: true,
						realtime: true,
						xAxisIndex: [0, 1, 2],
						bottom: this.legendPosition == 'top' ? 10 : 50,
						dataBackground: {
							areaStyle: {
								opacity: 0.1,
							}
						},
						labelFormatter: function (value, valueStr) {
							const date = new Date(valueStr);
							return `${chartDateStr(date)}\n${chartTimeStr(date)}`
						}
					},
					// For touchscreen
					{
						type: 'inside',
						disabled: false,
						realtime: true,
						xAxisIndex: [0, 1, 2]
					},
				],
				grid: [ // need for padding control (make smaller padding)
					{ left: 60, right: 120, top: 60, bottom: 120 },
				],
				tooltip: {
					trigger: 'axis',
					// Tooltip builder function
					formatter: (params/*, ticket, callback*/) => {
						const parseData = (data) => data === 'NaN' ? '-' : Number(data).toFixed(2)

						// Get first valid date
						let date = new Date()
						for (let index = 0; index < params.length; index++) {
							if (params[index]) {
								date = new Date(new Date(params[index].axisValue).getTime())
								break
							}
						}

						// Addtooltip header
						let tooltip = `<div class="container">`
						tooltip += `<div class="row"><div class="col text-center">`
						tooltip += ` ${chartDateStr(date)} ${chartTimeStr(date)}`
						tooltip += `</div></div>`

						// Add tooltip body
						const lines = [
							{
								iValue: this.$options.Vi,
								iMin: this.$options.VconfidenceLineIndex,
								iMax: this.$options.VconfidenceRangeIndex,
							},
							{
								iValue: this.$options.Ii,
								iMin: this.$options.IconfidenceLineIndex,
								iMax: this.$options.IconfidenceRangeIndex,
							},
							{
								iValue: this.$options.Ei,
								iMin: this.$options.EconfidenceLineIndex,
								iMax: this.$options.EconfidenceRangeIndex,
							},
						]
						for (const line of lines) {
							const parm = params[line.iValue]
							const min = params[line.iMin]
							const max = params[line.iMax]
							if (!parm || !min || !max) {
								continue
							}
							tooltip += `<div class="row justify-content-between">`
								tooltip += `<div class="col my-0 py-0 text-start">`
									tooltip += `<span style="color: ${parm.color};">&#9210; </span>`
									tooltip += `${parm.seriesName}`
								tooltip += `</div>`
								tooltip += `<div class="col-auto my-0 py-0 ms-auto me-0 text-end fw-bolder">`
									tooltip += `${parseData(parm.data[1])} `
									if (Number(parm.data[1]) >= 0) {
										tooltip += `(мін = ${parseData(min.data[1])}, макс = ${parseData((Number(min.data[1]) + Number(max.data[1])))})`
									} else {
										tooltip += `(мін = ${parseData((Number(min.data[1]) + Number(max.data[1])))}, макс = ${parseData(min.data[1])})`
									}
									// tooltip += `(мін = ${parseData(min.data[1])}, макс = ${parseData(max.data[1])} )`
								tooltip += `</div>`
							tooltip += `</div>`
						}

						tooltip += `</div>`
						return tooltip
					}
				},
				toolbox: [
					{
						show: this.isExpandable,
						// itemSize: '21',
						top: 5,
						right: 5,
						feature: {
							myExpand: {
								show: false,
								// title: 'Розгорнути',
								icon: 'path://M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5M.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5m15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5',
								onclick: () => {},
							},
							myCollapse: {
								show: false,
								// title: 'Згорнути',
								icon: 'path://M5.5 0a.5.5 0 0 1 .5.5v4A1.5 1.5 0 0 1 4.5 6h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5m5 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 10 4.5v-4a.5.5 0 0 1 .5-.5M0 10.5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 6 11.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5m10 1a1.5 1.5 0 0 1 1.5-1.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0z',
								onclick: () => {},
							},
						},
					},
					{
						show: true,
						top: 5,
						left: 5,
						itemSize: 16,
						feature: {
							mySettings: {
								show: true,
								title: '',
								icon: 'path://M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0 M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z',
								onclick: () => { this.showSettings = true },
							},
						},
						emphasis: {
							iconStyle: {
								textPosition: 'left',
								textAlign: 'left',
							},
						},
					},
					{
						show: true,
						top: 7,
						left: 45,
						itemSize: 16,
						feature: {
							myUpdate: {
								show: true,
								title: 'Оновити',
								icon: 'path://M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466',
								onclick: () => { this.readValues() },
							},
						},
						iconStyle: {
							color: '#6c757d',
							borderColor: '#6c757d',
						},
						// On mouse move style
						emphasis: {
							iconStyle: {
								color: '#3e98c5',
								borderColor: '#3e98c5',
							},
						},
					}
				],
				xAxis: [
					{
						gridIndex: 0, // attach to grid
						type: 'time',
						boundaryGap: false,
						data: [],
						axisLine: {
							show: false, // for compatibility with change yAxis settings on click
						},
						axisTick: {
							show: false, // for compatibility with change yAxis settings on click
						},
						axisLabel: {
							hideOverlap: true,

							formatter: (value, /*idx*/) => {
								var local = new Date(new Date(value).getTime())

								// If period > 6 days
								if (periodDays(this.timeFrom, this.timeTo) > 6) {
									return `${chartDateStr(local)}`
								}
								// If first axis label in chart or first label in day
								else if(
									xAxisDayTmp == null
									|| local.getDate() != xAxisDayTmp.getDate()
								) {
									xAxisDayTmp = local
									return `${chartDateStr(local)}\n${chartTimeStr(local)}`
								} else {
									return `${chartTimeStr(local)}`
								}
							}
						},
					}
				],
				yAxis: [
					// Voltage
					{
						gridIndex: 0, // attach to grid
						type: 'value',
						triggerEvent: true, // for yAxis.on events handle
						//name: this.$options.VOLTAGE_AXIS_NAME,
						name: 'Uout',
						position: 'right',
						alignTicks: false,
						// Vertical line of axis
						axisLine: {
							show: true,
							lineStyle: {
								//color: this.$options.VOLTAGE_COLOR,
								color: this.$options.TITLE_COLOR,
							}
						},
						// Grid lines of axis
						splitLine: {
							show: true,
						},
						axisLabel: {
							formatter: (v) => {
								return Number(v) === 0 ? 0 : (Number(v).toFixed(1) + ' В')
							},
						},
						axisPointer: {
							snap: true
						}
					},
					// Curent
					{
						gridIndex: 0,
						type: 'value',
						triggerEvent: true, // for yAxis.on events handle
						//name: this.$options.CURENT_AXIS_NAME,
						name: 'Iout',
						position: 'right',
						offset: 70,
						alignTicks: false,
						// Vertical line of axis
						axisLine: {
							show: true,
							lineStyle: {
								//color: this.$options.CURENT_COLOR,
								color: this.$options.TITLE_COLOR,
							}
						},
						// Grid lines of axis
						splitLine: {
							show: true,
						},
						axisLabel: {
							formatter: (v) => {
								return Number(v) === 0 ? 0 : (Number(v).toFixed(1) + ' А')
							},
						},
						axisPointer: {
							snap: true
						}
					},
					// Potential
					{
						// show: false,
						gridIndex: 0,
						type: 'value',
						triggerEvent: true, // for yAxis.on events handle
						//name: this.$options.POTENTIAL_AXIS_NAME,
						name: 'Eout',
						nameGap: 35,
						nameLocation: 'center',
						position: 'left',
						alignTicks: false,
						min: this.$options.eAxisRange[0],
						max: this.$options.eAxisRange[1],
						// Vertical line of axis
						axisLine: {
							show: true,
							lineStyle: {
								//color: this.$options.POTENTIAL_COLOR,
								color: this.$options.TITLE_COLOR,
							}
						},
						// Grid lines of axis
						splitLine: {
							show: true,
						},
						axisLabel: {
							formatter: (v) => {
								return Number(v) === 0 ? 0 : (Number(v).toFixed(1) + '')
							},
						},
						axisPointer: {
							snap: true
						}
					},
				],
				series: [
					// Voltage
					{
						name: this.$options.VOLTAGE_AXIS_NAME,
						type: 'line',
						xAxisIndex: 0, // attach to x axis
						yAxisIndex: this.$options.vAxisIndex, // attach to y axis
						data: [],
						smooth: false,
						lineStyle: {
							width: 1,
						},
						showSymbol: false, 
						emphasis: {
							disabled: true, // Disable effect on hover 
						},
					},
					// Voltage confidence band line
					{
						name: this.$options.VOLTAGE_AXIS_NAME + 'confidence_line', // for visibility on/off
						type: 'line',
						xAxisIndex: 0, // attach to x axis
						yAxisIndex: this.$options.vAxisIndex, // attach to y axis
						data: [],
						smooth: false,
						lineStyle: {
							opacity: 0
						},
						stack: 'voltage-band',
						symbol: 'none',
						// Hide in legend
						legendHoverLink: false,
						showSymbol: false,
						tooltip: { show: true },
						legend: { show: false },
						//
					},
					// Voltage confidence band range
					{
						name: this.$options.VOLTAGE_AXIS_NAME + 'confidence_range', // for visibility on/off
						type: 'line',
						xAxisIndex: 0, // attach to x axis
						yAxisIndex: this.$options.vAxisIndex, // attach to y axis
						data: [],
						smooth: false,
						lineStyle: {
							opacity: 0
						},
						stack: 'voltage-band',
						symbol: 'none',
						areaStyle: { // enable area gradient (by visual map)
							color: this.$options.VOLTAGE_COLOR,
							opacity: 0.15,
						},
					},

					// Curent
					{
						name: this.$options.CURENT_AXIS_NAME,
						type: 'line',
						xAxisIndex: 0,
						yAxisIndex: this.$options.iAxisIndex,
						data: [],
						smooth: false,
						lineStyle: {
							width: 1,
						},
						showSymbol: false,
						emphasis: {
							disabled: true, // Disable effect on hover 
						},
					},
					// Curent confidence band line
					{
						name: this.$options.CURENT_AXIS_NAME + 'confidence_line', // for visibility on/off
						type: 'line',
						xAxisIndex: 0, // attach to x axis
						yAxisIndex: this.$options.iAxisIndex, // attach to y axis
						data: [],
						smooth: false,
						lineStyle: {
							opacity: 0
						},
						stack: 'curent-band',
						symbol: 'none',
						// Hide in legend
						legendHoverLink: false,
						showSymbol: false,
						tooltip: { show: true },
						legend: { show: false },
						//
					},
					// Curent confidence band range
					{
						name: this.$options.CURENT_AXIS_NAME + 'confidence_range', // for visibility on/off
						type: 'line',
						xAxisIndex: 0, // attach to x axis
						yAxisIndex: this.$options.iAxisIndex, // attach to y axis
						data: [],
						smooth: false,
						lineStyle: {
							opacity: 0
						},
						stack: 'curent-band',
						symbol: 'none',
						areaStyle: { // enable area gradient (by visual map)
							color: this.$options.CURENT_COLOR,
							opacity: 0.15,
						},
					},

					// Potential
					{
						name: this.$options.POTENTIAL_AXIS_NAME,
						type: 'line',
						xAxisIndex: 0,
						yAxisIndex: this.$options.eAxisIndex,
						data: [],
						smooth: false,
						lineStyle: {
							width: 1,
						},
						showSymbol: false,
						emphasis: {
							disabled: true, // Disable effect on hover 
						},
					},
					// Potential confidence band line
					{
						name: this.$options.POTENTIAL_AXIS_NAME + 'confidence_line', // for visibility on/off
						type: 'line',
						xAxisIndex: 0, // attach to x axis
						yAxisIndex: this.$options.eAxisIndex, // attach to y axis
						data: [],
						smooth: false,
						lineStyle: {
							opacity: 0
						},
						stack: 'potential-band',
						symbol: 'none',
						// Hide in legend
						legendHoverLink: false,
						showSymbol: false,
						tooltip: { show: true },
						legend: { show: false },
					},
					// Potential confidence band range
					{
						name: this.$options.POTENTIAL_AXIS_NAME + 'confidence_range', // for visibility on/off
						type: 'line',
						xAxisIndex: 0, // attach to x axis
						yAxisIndex: this.$options.eAxisIndex, // attach to y axis
						data: [],
						smooth: false,
						lineStyle: {
							opacity: 0
						},
						stack: 'potential-band',
						symbol: 'none',
						//
						areaStyle: { // enable area gradient (by visual map)
							color: this.$options.POTENTIAL_COLOR,
							opacity: 0.15,
						},
					},
					
				]
			},

			// Chart settings
			showSettings: false,
			//
			isLoading: false,

			altModifier: false, // true if any alt button pressed
			ctrlModifier: false, // true if any ctrl button pressed
		}
	},

	watch: {
		// Update chart on station id update
		stationId(/*newCriterias*/) {
			//console.log('Update stationId')
			if (!this.isLoading) {
				//this.readValues()
			}
		},
		// Update chart on timeFrom update
		timeFrom(/*newTime, oldTime*/) {
			// console.log('Update time from')
			if (!this.isLoading) {
				this.readValues()
			}
		},
		// Update chart on timeTo update
		timeTo(/*newCriterias*/) {
			// console.log('Update time to')
			if (!this.isLoading) {
				this.readValues()
			}
		},
		isDataZoom(newValue) {
			//console.log('Update isDataZoom to', newValue)
			if (!this.isLoading) {
				this.setDataZoomVisibility(newValue)
			}
		},
	},

	created() {},

	async mounted() {
		// Listen Alt keys
		window.addEventListener('keydown', this.onKeyDown)
		window.addEventListener('keyup', this.onKeyUp)

		//console.log('props: ', this.timeFrom, this.timeTo)
		// Set emit functions
		if (this.isExpand) {
			this.option.toolbox[0].feature.myCollapse.show = true
			this.option.toolbox[0].feature.myCollapse.onclick = () => {
				this.$emit('collapse')
			}
		} else {
			this.option.toolbox[0].feature.myExpand.show = true
			this.option.toolbox[0].feature.myExpand.onclick = () => {
				this.$emit('expand')
			}
		}

		// Set legend position
		switch (this.legendPosition) {
			case 'top':
				this.option.legend.top = 9
				this.option.legend.show = true
				
				this.option.grid[0].bottom = 60
				break
			case 'bottom':
				this.option.legend.bottom = 5
				this.option.legend.show = true
				break
			default:
				this.option.legend.top = 5
				this.option.legend.show = true
		}

		await this.readValues()
		//console.log("Fetch voltage: ", voltage)

		this.setDataZoomVisibility(this.isDataZoom)

		// If no chart settings
		if (this.isLineVisible('curent') === undefined) {
			this.settings.curent = true
		}
		if (this.isLineVisible('voltage') === undefined) {
			this.settings.voltage = true
		}
		if (this.isLineVisible('potential') === undefined) {
			this.settings.potential = true
		}

		// Add handlers on every yAxis in chart
		for (const axInd of [this.$options.vAxisIndex, this.$options.iAxisIndex, this.$options.eAxisIndex]) {
			// On mouse over switch pointer icon to custom
			this.chartInstance.on('mousemove', { yAxisIndex: axInd }, () => {
				this.chartInstance.getZr().painter.getViewportRoot().style.cursor =
					'url("data:image/svg+xml;utf8,' +
					encodeURIComponent(
						`<svg xmlns='http://www.w3.org/2000/svg' width='32' height='48'>
							<style>
								text { font-family: sans-serif; font-size: 9px; fill: black; }
							</style>
							<!-- White background -->
							<path
								d="M8.5 4.466V1.75a1.75 1.75 0 1 0-3.5 0v5.34l-1.2.24a1.5 1.5 0 0 0-1.196 1.636l.345 3.106a2.5 2.5 0 0 0 .405 1.11l1.433 2.15A1.5 1.5 0 0 0 6.035 16h6.385a1.5 1.5 0 0 0 1.302-.756l1.395-2.441a3.5 3.5 0 0 0 .444-1.389l.271-2.715a2 2 0 0 0-1.99-2.199h-.581a5 5 0 0 0-.195-.248c-.191-.229-.51-.568-.88-.716-.364-.146-.846-.132-1.158-.108l-.132.012a1.26 1.26 0 0 0-.56-.642 2.6 2.6 0 0 0-.738-.288c-.31-.062-.739-.058-1.05-.046z"
								fill='white'
							/>
							<!-- Pointer -->
							<path
								d='M6.75 1a.75.75 0 0 1 .75.75V8a.5.5 0 0 0 1 0V5.467l.086-.004c.317-.012.637-.008.816.027.134.027.294.096.448.182.077.042.15.147.15.314V8a.5.5 0 1 0 1 0V6.435l.106-.01c.316-.024.584-.01.708.04.118.046.3.207.486.43.081.096.15.19.2.259V8.5a.5.5 0 0 0 1 0v-1h.342a1 1 0 0 1 .995 1.1l-.271 2.715a2.5 2.5 0 0 1-.317.991l-1.395 2.442a.5.5 0 0 1-.434.252H6.035a.5.5 0 0 1-.416-.223l-1.433-2.15a1.5 1.5 0 0 1-.243-.666l-.345-3.105a.5.5 0 0 1 .399-.546L5 8.11V9a.5.5 0 0 0 1 0V1.75A.75.75 0 0 1 6.75 1M8.5 4.466V1.75a1.75 1.75 0 1 0-3.5 0v5.34l-1.2.24a1.5 1.5 0 0 0-1.196 1.636l.345 3.106a2.5 2.5 0 0 0 .405 1.11l1.433 2.15A1.5 1.5 0 0 0 6.035 16h6.385a1.5 1.5 0 0 0 1.302-.756l1.395-2.441a3.5 3.5 0 0 0 .444-1.389l.271-2.715a2 2 0 0 0-1.99-2.199h-.581a5 5 0 0 0-.195-.248c-.191-.229-.51-.568-.88-.716-.364-.146-.846-.132-1.158-.108l-.132.012a1.26 1.26 0 0 0-.56-.642 2.6 2.6 0 0 0-.738-.288c-.31-.062-.739-.058-1.05-.046zm2.094 2.025'
								fill='black'
							/>
							<!-- Text lines -->
							<text x='4' y='26'>Ctrl</text>
							<text x='4' y='36'>Alt</text>
						</svg>`
					) +
					'") 8 2, pointer'
			})

			// On mouse down (and hold) shift or scale yAxis
			this.chartInstance.on('mousedown', { yAxisIndex: axInd }, (params, axisIndex = axInd) => {
				// console.log('On chart mousedown at yAxis', params)
				const zr = this.chartInstance.getZr()
				let startY = params.event.offsetY
				const option = this.chartInstance.getOption()
				// yAxis model
				const axisModel = this.chartInstance.getModel().getComponent('yAxis', axisIndex)
				const scale = axisModel.axis.scale;
				const extent = scale.getExtent() // [min, max] current actual limits
				let startMin = extent[0]
				let startMax = extent[1]
				const range = startMax - startMin
				// Mouse move handler (depends on Al or Control button pressed)
				let moveHandler = () => {}

				// If Alt key is pressed
				if (this.altModifier) {
					moveHandler = (e) => {
						const deltaY = e.offsetY - startY
						const shift = (deltaY / 1000) * range // sensitivity coefficient
						const newMin = startMin + shift
						const newMax = startMax + shift
						option.yAxis[axisIndex].min = newMin
						option.yAxis[axisIndex].max = newMax
						this.chartInstance.setOption(option, false)
					}
				}
				// If Alt key is pressed
				else if (this.ctrlModifier) {
					moveHandler = (e) => {
						const deltaY = e.offsetY - startY
						// scale factor, larger deltaY means stronger scaling
						const scaleFactor = 1 - deltaY / 500
						if (scaleFactor <= 0) {
							return
						}

						const center = (startMax + startMin) / 2
						const half = (range / 2) * scaleFactor
						const newMin = center - half
						const newMax = center + half

						option.yAxis[axisIndex].min = newMin
						option.yAxis[axisIndex].max = newMax
						this.chartInstance.setOption(option, false)
					}
				} else {
					return
				}

				const upHandler = () => {
					zr.off('mousemove', moveHandler)
					zr.off('mouseup', upHandler)
				}

				zr.on('mousemove', moveHandler)
				zr.on('mouseup', upHandler)
			})
		}

		// On click chart area handler.
		// Change y axis settings
		this.chartInstance.getZr().on('click', (params) => {
			const pointInPixel = [params.offsetX, params.offsetY]

			// Get chart grid coordinates
			const gridModel = this.chartInstance.getModel().getComponent('grid', 0)
			const gridRect = gridModel.coordinateSystem.getRect()

			// Check if click is in chart rect zone by Y coordinate
			if (pointInPixel[1] >= gridRect.y
				&& pointInPixel[1] <= gridRect.y + gridRect.height
			) {
				// If click on Eout axis
				if (pointInPixel[0] >= gridRect.x - 50
					&& pointInPixel[0] <= gridRect.x
				) {
					// Swap default values and undefined (for automatic alignment)
					if (this.option.yAxis[this.$options.eAxisIndex].min === undefined) {
						this.option.yAxis[this.$options.eAxisIndex].min = this.$options.eAxisRange[0]
						this.option.yAxis[this.$options.eAxisIndex].max = this.$options.eAxisRange[1]
					} else {
						this.option.yAxis[this.$options.eAxisIndex].min = undefined
						this.option.yAxis[this.$options.eAxisIndex].max = undefined
					}
					return
				}

				// If click on Uout axis
				if (pointInPixel[0] >= gridRect.x + gridRect.width
					&& pointInPixel[0] <= gridRect.x + gridRect.width + 50
				) {
					// Swap default values and undefined (for automatic alignment)
					if (this.option.yAxis[this.$options.vAxisIndex].min === undefined) {
						this.option.yAxis[this.$options.vAxisIndex].min = 'dataMin'
						this.option.yAxis[this.$options.vAxisIndex].max = 'dataMax'
					} else {
						this.option.yAxis[this.$options.vAxisIndex].min = undefined
						this.option.yAxis[this.$options.vAxisIndex].max = undefined
					}
					return
				}

				// If click on Iout axis
				if (pointInPixel[0] >= gridRect.x + gridRect.width + 70
					&& pointInPixel[0] <= gridRect.x + gridRect.width + 120
				) {
					// Swap default values and undefined (for automatic alignment)
					if (this.option.yAxis[this.$options.iAxisIndex].min === undefined) {
						this.option.yAxis[this.$options.iAxisIndex].min = 'dataMin'
						this.option.yAxis[this.$options.iAxisIndex].max = 'dataMax'
					} else {
						this.option.yAxis[this.$options.iAxisIndex].min = undefined
						this.option.yAxis[this.$options.iAxisIndex].max = undefined
					}
					return
				}
			}
		})

		// // On mouse over chart area handler.
		// // Change mose cursor settings
		// this.chartInstance.getZr().on('mousemove', (params) => {
		// 	const pointInPixel = [params.offsetX, params.offsetY]
		// 	const gridModel = this.chartInstance.getModel().getComponent('grid', 0)
		// 	const gridRect = gridModel.coordinateSystem.getRect()

		// 	let inAxis = false

		// 	// Eout
		// 	if (
		// 		pointInPixel[1] >= gridRect.y &&
		// 		pointInPixel[1] <= gridRect.y + gridRect.height &&
		// 		pointInPixel[0] >= gridRect.x - 50 &&
		// 		pointInPixel[0] <= gridRect.x
		// 	) {
		// 		inAxis = true
		// 	}

		// 	// Uout
		// 	if (
		// 		pointInPixel[1] >= gridRect.y &&
		// 		pointInPixel[1] <= gridRect.y + gridRect.height &&
		// 		pointInPixel[0] >= gridRect.x + gridRect.width &&
		// 		pointInPixel[0] <= gridRect.x + gridRect.width + 50
		// 	) {
		// 		inAxis = true
		// 	}

		// 	// Iout
		// 	if (
		// 		pointInPixel[1] >= gridRect.y &&
		// 		pointInPixel[1] <= gridRect.y + gridRect.height &&
		// 		pointInPixel[0] >= gridRect.x + gridRect.width + 70 &&
		// 		pointInPixel[0] <= gridRect.x + gridRect.width + 120
		// 	) {
		// 		inAxis = true
		// 	}

		// 	// Chnge cursor
		// 	// this.chartInstance.getZr().setCursorStyle(inAxis ? 'pointer' : 'default')
		// 	if (inAxis) {
		// 		this.chartInstance.getZr().painter.getViewportRoot().style.cursor =
		// 			'url("data:image/svg+xml;utf8,' +
		// 			encodeURIComponent(
		// 				`<svg xmlns='http://www.w3.org/2000/svg' width='32' height='48'>
		// 					<style>
		// 						text { font-family: sans-serif; font-size: 9px; fill: black; }
		// 					</style>
		// 					<!-- White background -->
		// 					<path
		// 						d="M8.5 4.466V1.75a1.75 1.75 0 1 0-3.5 0v5.34l-1.2.24a1.5 1.5 0 0 0-1.196 1.636l.345 3.106a2.5 2.5 0 0 0 .405 1.11l1.433 2.15A1.5 1.5 0 0 0 6.035 16h6.385a1.5 1.5 0 0 0 1.302-.756l1.395-2.441a3.5 3.5 0 0 0 .444-1.389l.271-2.715a2 2 0 0 0-1.99-2.199h-.581a5 5 0 0 0-.195-.248c-.191-.229-.51-.568-.88-.716-.364-.146-.846-.132-1.158-.108l-.132.012a1.26 1.26 0 0 0-.56-.642 2.6 2.6 0 0 0-.738-.288c-.31-.062-.739-.058-1.05-.046z"
		// 						fill='white'
		// 					/>
		// 					<!-- Pointer -->
		// 					<path
		// 						d='M6.75 1a.75.75 0 0 1 .75.75V8a.5.5 0 0 0 1 0V5.467l.086-.004c.317-.012.637-.008.816.027.134.027.294.096.448.182.077.042.15.147.15.314V8a.5.5 0 1 0 1 0V6.435l.106-.01c.316-.024.584-.01.708.04.118.046.3.207.486.43.081.096.15.19.2.259V8.5a.5.5 0 0 0 1 0v-1h.342a1 1 0 0 1 .995 1.1l-.271 2.715a2.5 2.5 0 0 1-.317.991l-1.395 2.442a.5.5 0 0 1-.434.252H6.035a.5.5 0 0 1-.416-.223l-1.433-2.15a1.5 1.5 0 0 1-.243-.666l-.345-3.105a.5.5 0 0 1 .399-.546L5 8.11V9a.5.5 0 0 0 1 0V1.75A.75.75 0 0 1 6.75 1M8.5 4.466V1.75a1.75 1.75 0 1 0-3.5 0v5.34l-1.2.24a1.5 1.5 0 0 0-1.196 1.636l.345 3.106a2.5 2.5 0 0 0 .405 1.11l1.433 2.15A1.5 1.5 0 0 0 6.035 16h6.385a1.5 1.5 0 0 0 1.302-.756l1.395-2.441a3.5 3.5 0 0 0 .444-1.389l.271-2.715a2 2 0 0 0-1.99-2.199h-.581a5 5 0 0 0-.195-.248c-.191-.229-.51-.568-.88-.716-.364-.146-.846-.132-1.158-.108l-.132.012a1.26 1.26 0 0 0-.56-.642 2.6 2.6 0 0 0-.738-.288c-.31-.062-.739-.058-1.05-.046zm2.094 2.025'
		// 						fill='black'
		// 					/>
		// 					<!-- Text lines -->
		// 					<text x='4' y='26'>Ctrl</text>
		// 					<text x='4' y='36'>Alt</text>
		// 				</svg>`
		// 			) +
		// 			'") 8 2, pointer'
		// 	} else {
		// 		this.chartInstance.getZr().setCursorStyle('default')
		// 	}
		// })

		// On select or unselect some line in legend
		this.chartInstance.on('legendselectchanged', (params) => {
			const clickedName = params.name		// line name
			const selected = params.selected	// select or unselect (show or hide)
			// console.log('legendselectchanged', params)

			this.chartInstance.setOption({
				legend: {
					selected: {
						// Control the visibility min/max series synchronous
						[clickedName + 'confidence_line']: selected[clickedName],
						[clickedName + 'confidence_range']: selected[clickedName],
					}
				}
			})
		})
	},

	methods: {
		async readValues() {
			this.setLoading(true)

			let arr = []

			// Define metrics for read
			let unitNames = [INFLUXDB.station_metrics.power_unit, ]
			let metricNames = [PowerUnit.metricKeys.uOut, PowerUnit.metricKeys.iOut, ]		
			if (this.station.uProtectSource == 'power') {
				metricNames.push(PowerUnit.metricKeys.eOut)
			} else if (this.station.uProtectSource == 'service') {
				unitNames.push(INFLUXDB.station_metrics.service_unit)
				metricNames.push(ServiceUnit.metricKeys.eAvg)
			} else {
				this.setLineVisibility('potential', false)
			}

			// Define time period for read
			let chartTimeFrom = this.timeFrom
			let chartTimeTo = this.timeTo
			// const option = this.chartInstance?.getOption()
			// const dataZoom = option?.dataZoom[0]
			// if (dataZoom) {
			// 	chartTimeFrom = new Date(dataZoom?.startValue)
			// 	chartTimeTo = new Date( dataZoom?.endValue)
			// }
			

			await readStationMetrics(
				this.station.passport.get(Station.passportKeys.metricsId),
				unitNames, 
				metricNames,
				arr,
				chartTimeFrom,
				chartTimeTo,
				undefined,
				['min', 'max', 'median',],
				30000,
			).then(result => {
				// console.log("Fetch result: ", result, arr)
				if (!result) {
					return
				}

				// Sort by time
				arr.sort((a, b) => a.time.getTime() > b.time.getTime())
				
				// Fill x axis
				this.option.xAxis[0].data = arr.map((el) => el.time)

				// Fill y axis lines data
				// Fill previous data if no data in current time point
				let prevValue = null
				const makeValue = (time, val) => {
					if (val === 'NaN' && prevValue != null) {
						return [time, prevValue]
					} else {
						prevValue = val
						return [time, val]
					}
				}
				//
				prevValue = null
				this.option.series[this.$options.Vi].data = arr.map((el) => {
					return makeValue(
						el.time,
						Number(el[INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.uOut + '_median']).toFixed(2)
					)
					
				})
				prevValue = null
				this.option.series[this.$options.VconfidenceLineIndex].data = arr.map((el) => {
					return makeValue(
						el.time,
						Number(el[INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.uOut + '_min']).toFixed(2)
					)
				})
				prevValue = null
				this.option.series[this.$options.VconfidenceRangeIndex].data = arr.map((el) => {
					const keyMax = INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.uOut + '_max'
					const keyMin = INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.uOut + '_min'
					const val = Number(el[keyMax]-el[keyMin]).toFixed(2)

					return makeValue(
						el.time,
						val
					)
				})
				//
				prevValue = null
				this.option.series[this.$options.Ii].data = arr.map((el) => {
					return makeValue(
						el.time,
						Number(el[INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.iOut + '_median']).toFixed(2)
					)
				})
				prevValue = null
				this.option.series[this.$options.IconfidenceLineIndex].data = arr.map((el) => {
					return makeValue(
						el.time,
						Number(el[INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.iOut + '_min']).toFixed(2)
					)
				})
				prevValue = null
				this.option.series[this.$options.IconfidenceRangeIndex].data = arr.map((el) => {
					const keyMax = INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.iOut + '_max'
					const keyMin = INFLUXDB.station_metrics.power_unit + '_' + PowerUnit.metricKeys.iOut + '_min'
					const val = Number(el[keyMax] - el[keyMin]).toFixed(2)

					return makeValue(
						el.time,
						val
					)
				})
				//
				let potentialSrcUnit = null
				if (this.station.uProtectSource == 'power') {
					potentialSrcUnit = INFLUXDB.station_metrics.power_unit
				} else if (this.station.uProtectSource == 'service') {
					potentialSrcUnit = INFLUXDB.station_metrics.service_unit
				}
				prevValue = null
				this.option.series[this.$options.Ei].data = arr.map((el) => {
					return makeValue(
						el.time,
						Number(el[potentialSrcUnit + '_' + PowerUnit.metricKeys.eOut + '_median']).toFixed(2)
					)
				})
				prevValue = null
				this.option.series[this.$options.EconfidenceRangeIndex].data = arr.map((el) => {
					const keyMax = potentialSrcUnit + '_' + PowerUnit.metricKeys.eOut + '_max'
					const keyMin = potentialSrcUnit + '_' + PowerUnit.metricKeys.eOut + '_min'
					let val = Math.abs(Number(el[keyMax]) - Number(el[keyMin])).toFixed(2)

					return makeValue(
						el.time,
						-val,
					)
				})
				prevValue = null
				this.option.series[this.$options.EconfidenceLineIndex].data = arr.map((el) => {
					return makeValue(
						el.time,
						Number(el[potentialSrcUnit + '_' + PowerUnit.metricKeys.eOut + '_max']),
					)
				})
			}).catch(err => {
				console.error("Error on read station voltage: ", err)
			})

			// Render only visible lines
			if (this.isLineVisible('curent') === false) {
				this.setLineVisibility('curent', false)
			}
			if (this.isLineVisible('voltage') === false) {
				this.setLineVisibility('voltage', false)
			}
			if (this.isLineVisible('potential') === false) {
				this.setLineVisibility('potential', false)
			}

			this.setLoading(false)
		},

		setLoading(loadingState) {
			this.isLoading = Boolean(loadingState)

			this.$emit('loading', this.isLoading)

			if (this.isLoading) {
				this.chartInstance?.showLoading(
					'default',
					{
						text: '',
						spinnerRadius: 20,
						color: '#2c3e50', // spiner color
						maskColor: 'rgba(255, 255, 255, 0.8)', // background
					}
				)
			} else {
				this.chartInstance?.hideLoading()
			}
		},

		setDataZoomVisibility(isVisible) {
			if (isVisible) {
				this.option.grid[0].bottom = this.legendPosition == 'top' ? 80 : 120
				this.option.dataZoom[0].show = true
				this.option.dataZoom[1].disabled = false
			} else {
				this.option.dataZoom[0].show = false
				this.option.grid[0].bottom = 60
				this.option.dataZoom[1].disabled = true
			}
		},

		setLineVisibility(line, isVisible) {
			let lineIndex, axisIndex, legendDataName
			switch (line) {
				case 'curent':
					lineIndex = this.$options.Ii
					axisIndex = this.$options.iAxisIndex
					legendDataName = this.$options.CURENT_AXIS_NAME
					break
				case 'voltage':
					lineIndex = this.$options.Vi
					axisIndex = this.$options.vAxisIndex
					legendDataName = this.$options.VOLTAGE_AXIS_NAME
					break
				case 'potential':
					lineIndex = this.$options.Ei
					axisIndex = this.$options.eAxisIndex
					legendDataName = this.$options.POTENTIAL_AXIS_NAME
					break
				default:
					return
			}
			
			// Show / hide axis
			this.option.yAxis[axisIndex].show = isVisible

			if (isVisible) {
				// Show data
				this.option.series[lineIndex].data = this.option.series[lineIndex].hidenData
				delete this.option.series[lineIndex].hidenData
			} else {
				// Hide data
				this.option.series[lineIndex].hidenData = this.option.series[lineIndex].data
				delete this.option.series[lineIndex].data
				this.option.series[lineIndex].data = []
			}

			// Save to settings
			this.settings[line] = isVisible

			// Show hide in legend
			this.chartInstance.setOption({
				legend: {
					selected: {
						[legendDataName]: isVisible,
						// Control the visibility min/max series synchronous
						[legendDataName + 'confidence_line']: isVisible,
						[legendDataName + 'confidence_range']: isVisible,
					}
				}
			})
		},

		isLineVisible(line) {
			return this.settings[line]
		},

		onKeyDown(e) {
			if (e.code === 'AltLeft' || e.code === 'AltRight') {
				this.altModifier = true
			}

			if (e.code === 'ControlLeft' || e.code === 'ControlRight') {
				this.ctrlModifier = true
			}
		},

		onKeyUp(e) {
			if (e.code === 'AltLeft' || e.code === 'AltRight') {
				this.altModifier = false
			}

			if (e.code === 'ControlLeft' || e.code === 'ControlRight') {
				this.ctrlModifier = false
			}
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		settings() {
			return this.sessionStore.getChartAxis(this.stationId, 'voltageCurentPotential')
		},

		chartInstance() {
			return this.$refs.chart?.chart
		},

		isNoPotentialSource() {
			return this.station.uProtectSource === undefined || this.station.uProtectSource === null
		},

		chartWidth() {
			return this.$refs.chart?.chart?.getModel()?.getComponent('grid', 0)?.coordinateSystem?.getRect()?.width
		},
	},

	beforeUnmount() {
		// Remove event listeners
		window.removeEventListener('keydown', this.onKeyDown)
		window.removeEventListener('keyup', this.onKeyUp)
	},
}
</script>




<style scoped>
	.chart {
		height: 100%;
	}
</style>